import type { ChatMessage } from './types'

// What each ui_call in an Insights conversation did, read from the
// conversation itself (#1534). The model's call is on an assistant row, the
// browser's result is the role 'tool' row that answers it. The pane draws a
// caption for each, and a Confirm / Cancel pair for a change the user has to
// approve.

export const UI_SEARCH_TOOL = 'ui_search'
export const UI_CALL_TOOL = 'ui_call'
export const isBridgeTool = (name: string) => name === UI_SEARCH_TOOL || name === UI_CALL_TOOL

export type UiCallStatus =
  | 'pending' // no result yet: the browser is still running it
  | 'done'
  | 'failed'
  | 'needs_confirmation' // waiting for the user's Confirm or Cancel
  | 'asked' // an ask tool proposed a change for the model to put to the user in words; nothing changed
  | 'confirming'
  | 'confirmed'
  | 'cancelled'
  | 'expired' // no longer actionable: the user moved on, the page changed, or it came from an earlier session

/** What the user decided about a needs_confirmation call. Kept by the hook. */
export interface UiCallResolution {
  status: 'confirming' | 'confirmed' | 'cancelled' | 'failed'
  message?: string
}

export interface UiCallView {
  id: string
  tool: string
  args: Record<string, unknown>
  status: UiCallStatus
  /** One line for the pane. */
  caption: string
  /** The question to approve, for needs_confirmation. */
  question?: string
  confirmLabel?: string
  /**
   * What Confirm runs: the arguments the question was asked about, fully
   * resolved by the tool (falls back to the model's args).
   */
  confirmArgs?: Record<string, unknown>
  /**
   * The page tool Confirm runs, when the tool's answer offers a different
   * change from the one asked for (a refused rename offers a copy into My
   * Tables, run by duplicate_table). Falls back to `tool`.
   */
  confirmTool?: string
}

export interface SummarizeOptions {
  /**
   * Whether this pane session produced the call. A question restored from the
   * server's cache (the pane reopened, the page reloaded) is never live.
   * Default: every call is.
   */
  actionable?: (callId: string) => boolean
  /** What the page shows now (the open table). A question about another lapses. */
  scope?: string
}

export const EXPIRED_CAPTION = 'This request has expired — ask again.'

function parse(s: string | undefined): Record<string, unknown> | null {
  if (!s) return null
  try {
    const v = JSON.parse(s)
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** `set_quick_filter` → `set quick filter`. */
export function toolPhrase(tool: string): string {
  return tool.replace(/_/g, ' ')
}

// The argument a person would want to see next to the tool's name.
function keyArg(args: Record<string, unknown>): string {
  for (const k of ['filter', 'new_name', 'name', 'value', 'chart_type', 'query', 'table', 'folder', 'item']) {
    const v = args[k]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

function doneCaption(tool: string, args: Record<string, unknown>, result: Record<string, unknown> | null): string {
  if (typeof result?.did === 'string' && result.did) return `Did: ${result.did}`
  const arg = keyArg(args)
  return `Did: ${toolPhrase(tool)}${arg ? ` — ${arg}` : ''}`
}

/**
 * Every ui_call in the conversation, by tool call id. `resolutions` are the
 * user's Confirm / Cancel decisions, which the conversation does not hold.
 */
export function summarizeUiCalls(
  messages: ChatMessage[],
  resolutions: Record<string, UiCallResolution> = {},
  options: SummarizeOptions = {},
): Record<string, UiCallView> {
  const results = new Map<string, string>()
  messages.forEach((m) => {
    if (m.role === 'tool' && m.tool_call_id) results.set(m.tool_call_id, m.content)
  })
  const out: Record<string, UiCallView> = {}
  messages.forEach((m, index) => {
    if (m.role !== 'assistant' || !m.tool_calls) return
    for (const call of m.tool_calls) {
      if (call.function.name !== UI_CALL_TOOL) continue
      const callArgs = parse(call.function.arguments) ?? {}
      const tool = typeof callArgs.tool === 'string' ? callArgs.tool : ''
      const args = (callArgs.args && typeof callArgs.args === 'object' ? callArgs.args : parse(String(callArgs.args ?? ''))) as Record<string, unknown> | null
      const base = { id: call.id, tool, args: args ?? {} }
      const raw = results.get(call.id)
      if (raw === undefined) {
        out[call.id] = { ...base, status: 'pending', caption: `Working: ${toolPhrase(tool)}…` }
        continue
      }
      const result = parse(raw)
      if (result?.needs_confirmation) {
        const question = typeof result.message === 'string' ? result.message : `${toolPhrase(tool)}?`
        const confirmLabel = typeof result.confirm_label === 'string' ? result.confirm_label : 'Confirm'
        const confirmArgs = result.args && typeof result.args === 'object' && !Array.isArray(result.args)
          ? (result.args as Record<string, unknown>)
          : base.args
        const confirmTool = typeof result.tool === 'string' && result.tool ? result.tool : tool
        const decided = resolutions[call.id]
        if (decided) {
          const caption =
            decided.status === 'confirming' ? `${question} Working…`
              : decided.status === 'confirmed' ? `Confirmed: ${decided.message ?? question}`
                : decided.status === 'cancelled' ? `Cancelled: ${question}`
                  : `Did not go through: ${decided.message ?? question}`
          out[call.id] = { ...base, status: decided.status, caption, question, confirmLabel, confirmArgs, confirmTool }
          continue
        }
        // Not live when: it came from an earlier session, the user has moved
        // on (a later message from them), or the open table has changed.
        const fromEarlierSession = options.actionable ? !options.actionable(call.id) : false
        const movedOn = messages.slice(index + 1).some((later) => later.role === 'user')
        const pageChanged = typeof result.scope === 'string' && options.scope !== undefined && result.scope !== options.scope
        out[call.id] = fromEarlierSession || movedOn || pageChanged
          ? { ...base, status: 'expired', caption: `${question} ${EXPIRED_CAPTION}`, question, confirmLabel, confirmArgs, confirmTool }
          : { ...base, status: 'needs_confirmation', caption: question, question, confirmLabel, confirmArgs, confirmTool }
        continue
      }
      // An ask tool (the quick filter) changed nothing: the model asks the
      // user in words. Not a Confirm button, and not "Did".
      if (result?.ask_user) {
        const meaning = typeof result.meaning === 'string' && result.meaning ? result.meaning : keyArg(base.args) || toolPhrase(tool)
        out[call.id] = { ...base, status: 'asked', caption: `Proposed: ${meaning}` }
        continue
      }
      if (result && typeof result.error === 'string') {
        out[call.id] = { ...base, status: 'failed', caption: `Couldn't ${toolPhrase(tool)}: ${result.error}` }
        continue
      }
      if (result?.needs_disambiguation) {
        out[call.id] = { ...base, status: 'done', caption: typeof result.message === 'string' ? result.message : `Several match — which one?` }
        continue
      }
      out[call.id] = { ...base, status: 'done', caption: doneCaption(tool, base.args, result) }
    }
  })
  return out
}

/** An assistant row that only searched the page and says nothing: hidden. */
export function isSearchOnlyRow(m: ChatMessage): boolean {
  return (
    m.role === 'assistant' &&
    !!m.tool_calls?.length &&
    m.tool_calls.every((c) => c.function.name === UI_SEARCH_TOOL) &&
    !(typeof m.content === 'string' && m.content.trim())
  )
}
